/**
 * Client-side staleness evaluation (plan §6.7 · FR-A8).
 *
 * `/v1/meta` returns 200 whether data is fresh or stale — staleness is a
 * banner, not an error code. The web layer evaluates four independent triggers
 * over the meta payload — plus a fifth `freshness_unevaluable` reason, which is
 * not a trigger but the absence of a verdict (ADR-0024) — and paints DATA STALE
 * + `[data-stale="true"]` when any fires. No-snapshot (`asOf: null`) is a
 * DISTINCT state and never reports as stale (plan §6.6 operational-state
 * contract).
 *
 * Membership and frozen axes use the US equity session calendar day in
 * `America/New_York` (plan R20 · §6.7a), matching ingest liveness "today".
 * `finished_at` stays on pure UTC milliseconds.
 *
 * AN UNREADABLE VALUE IS ITS OWN STATE — IT DOES NOT READ AS FRESH (ADR-0024).
 *
 * Each axis returns `fresh | stale | unevaluable`. `null` means the axis does
 * not apply and is genuinely fresh; a value that is PRESENT but cannot be
 * interpreted is `unevaluable` and raises `freshness_unevaluable`.
 *
 * This is reachable over the real wire, not only via a client bug:
 * `MarketDateSchema` is a shape-only regex whose own comment defers calendar
 * validity to ingest, so `/v1/meta` may legitimately Zod-parse a
 * `membershipAsOf` of `2026-02-30`. Under the previous fail-open reading that
 * silently disabled the membership trigger and the terminal rendered as
 * healthy — no banner, no `[data-stale]`, nothing logged — which is the exact
 * "a format change becomes wrong numbers instead of an alert" failure the
 * house style calls out, applied to the one affordance whose entire job is
 * announcing that the pipeline is dead.
 */

/**
 * Latest successful complete run 36h old OR OLDER (inclusive — an ingest at
 * exactly 36h reports stale; `isFinishedAtStale` uses `>=`).
 */
export const INGEST_STALE_MS = 36 * 60 * 60 * 1000;

/**
 * `membershipAsOf` STRICTLY older than 7 calendar days (exclusive — day 7 is
 * fresh, day 8 is stale). Deliberately not the same boundary style as
 * INGEST_STALE_MS: membership is a coarse daily fact, so the 7th day is still
 * a legitimate same-week value.
 */
export const MEMBERSHIP_STALE_DAYS = 7;

/**
 * No `lastDataAdvancedOn` movement across this many weekdays
 * (explicit v1 trade-off for not maintaining a trading calendar — ~6 calendar
 * days across a weekend).
 *
 * The budget INCLUDES the current market day, whose EOD ingest has not run
 * yet, so the banner fires one weekday earlier than "4 missed EOD cycles".
 * That is intentional slack, not an off-by-one: it is strictly earlier than
 * plan §6.2f's latency ceiling, and no US market holiday cluster spans four
 * consecutive weekdays, so it cannot produce a holiday false positive.
 */
export const FROZEN_FEED_WEEKDAYS = 4;

/** US equity session timezone for market-day "today" (plan R20). */
export const US_MARKET_TZ = 'America/New_York';

export type StaleReason =
  | 'stale_finished_at'
  | 'stale_data_as_of'
  | 'stale_membership'
  | 'frozen_data_advancement'
  /**
   * A value was PRESENT but could not be interpreted, so that axis could not
   * be evaluated (ADR-0024). Distinct from all four triggers above: those mean
   * "we checked and it is bad", this means "we could not check".
   */
  | 'freshness_unevaluable';

/** Per-axis outcome. `null` input is `fresh` — absent is not the same as unreadable. */
type AxisResult = 'fresh' | 'stale' | 'unevaluable';

export interface StalenessInput {
  readonly asOf: string | null;
  readonly lastIngestAt: string | null;
  readonly lastDataAdvancedOn: string | null;
  readonly lastOperationalTradingDay: string | null;
  readonly membershipAsOf: string | null;
}

export interface StalenessVerdict {
  readonly stale: boolean;
  readonly reasons: readonly StaleReason[];
}

/** Shared frozen verdict — the single "nothing is stale" value in the app. */
export const FRESH: StalenessVerdict = Object.freeze({
  stale: false,
  reasons: Object.freeze([]),
});

/**
 * Evaluate the four §6.7 triggers against a meta-shaped payload, plus the
 * §6.7 unevaluable state (ADR-0024).
 *
 * `now` is injectable so unit tests pin weekday arithmetic; production callers
 * omit it and use wall clock.
 */
export function evaluateStaleness(meta: StalenessInput, now: Date = new Date()): StalenessVerdict {
  /* No published snapshot — first-run / local empty state. Distinct from stale. */
  if (meta.asOf === null) {
    return FRESH;
  }

  const axes: readonly (readonly [StaleReason, AxisResult])[] = [
    ['stale_finished_at', finishedAtAxis(meta.lastIngestAt, now)],
    ['stale_data_as_of', dataAsOfAxis(meta.asOf, meta.lastOperationalTradingDay)],
    ['stale_membership', membershipAxis(meta.membershipAsOf, now)],
    ['frozen_data_advancement', frozenAdvancementAxis(meta.lastDataAdvancedOn, now)],
  ];

  const reasons: StaleReason[] = [];
  let anyUnevaluable = false;
  for (const [reason, result] of axes) {
    if (result === 'stale') reasons.push(reason);
    else if (result === 'unevaluable') anyUnevaluable = true;
  }

  /* Appended LAST so a real trigger always leads the banner copy: "we could
     not check" is the least actionable thing to say when we did check
     something and it was bad. */
  if (anyUnevaluable) reasons.push('freshness_unevaluable');

  if (reasons.length === 0) return FRESH;
  return { stale: true, reasons: Object.freeze(reasons) };
}

/**
 * US equity session calendar day as `YYYY-MM-DD` for `now` (plan R20 · §6.7a).
 * Used by membership age and frozen-feed weekday budgets — not by finished_at.
 */
export function usMarketDay(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: US_MARKET_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function finishedAtAxis(lastIngestAt: string | null, now: Date): AxisResult {
  if (lastIngestAt === null) return 'fresh';
  const ms = Date.parse(lastIngestAt);
  if (!Number.isFinite(ms)) return 'unevaluable';
  return now.getTime() - ms >= INGEST_STALE_MS ? 'stale' : 'fresh';
}

function dataAsOfAxis(asOf: string, lastOperationalTradingDay: string | null): AxisResult {
  if (lastOperationalTradingDay === null) return 'fresh';
  const asOfDay = parseMarketDateUtc(asOf);
  const opDay = parseMarketDateUtc(lastOperationalTradingDay);
  if (asOfDay === null || opDay === null) return 'unevaluable';
  return asOfDay.getTime() < opDay.getTime() ? 'stale' : 'fresh';
}

function membershipAxis(membershipAsOf: string | null, now: Date): AxisResult {
  if (membershipAsOf === null) return 'fresh';
  const asOfDay = parseMarketDateUtc(membershipAsOf);
  if (asOfDay === null) return 'unevaluable';
  const ageDays = diffUtcCalendarDays(asOfDay, marketTodayUtc(now));
  return ageDays > MEMBERSHIP_STALE_DAYS ? 'stale' : 'fresh';
}

function frozenAdvancementAxis(lastDataAdvancedOn: string | null, now: Date): AxisResult {
  if (lastDataAdvancedOn === null) return 'fresh';
  const advanced = parseMarketDateUtc(lastDataAdvancedOn);
  if (advanced === null) return 'unevaluable';
  const weekdays = countWeekdaysExclusive(advanced, marketTodayUtc(now), FROZEN_FEED_WEEKDAYS);
  return weekdays >= FROZEN_FEED_WEEKDAYS ? 'stale' : 'fresh';
}

/**
 * Today's US market day as a UTC midnight Date.
 *
 * Total by construction: `usMarketDay` always emits a regex-valid calendar
 * `YYYY-MM-DD` from Intl, so there is no null case to guard here (style §4 —
 * state an invariant guaranteed by local control flow, do not branch on it).
 */
function marketTodayUtc(now: Date): Date {
  return new Date(`${usMarketDay(now)}T00:00:00.000Z`);
}

/**
 * Parse `YYYY-MM-DD` as a UTC midnight Date; null on garbage or non-calendar
 * dates (e.g. `2026-02-30` rolls in Date.parse and must not count as a day).
 */
function parseMarketDateUtc(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const ms = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(ms)) return null;
  const parsed = new Date(ms);
  if (parsed.toISOString().slice(0, 10) !== value) return null;
  return parsed;
}

/** Whole calendar days between two UTC midnights (b - a). */
function diffUtcCalendarDays(a: Date, b: Date): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY);
}

/**
 * Count weekdays strictly after `from` up to and including `to`, stopping at
 * `cap`. Weekends do not consume the frozen-feed budget.
 *
 * The cap is load-bearing, not an optimisation. `MarketDateSchema` is a
 * shape-only regex, so a published `lastDataAdvancedOn` of `0001-01-01`
 * reaches this loop and would otherwise walk ~740k days — synchronously on the
 * render path, and again on every 60s `use-now-tick` bump. The caller only
 * ever compares against FROZEN_FEED_WEEKDAYS, so counting past it is dead work
 * that turns a stuck ingest value into a recurring main-thread stall.
 */
function countWeekdaysExclusive(from: Date, to: Date, cap: number): number {
  if (to.getTime() <= from.getTime()) return 0;
  let count = 0;
  const cursor = new Date(from.getTime());
  cursor.setUTCDate(cursor.getUTCDate() + 1);
  while (cursor.getTime() <= to.getTime() && count < cap) {
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}
